import { BASE_API_URL, fetchWithAuth } from "./api";

export interface ReskillingStudentInput {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  identification_type?: string;
  identification_number?: string;
  country?: string;
  city?: string;
  occupation?: string;
  education_level?: string;
  /** Slug del cliente cuya landing se usó. */
  cliente_slug?: string;
  /** Aceptación del consentimiento informado. */
  consentimiento?: boolean;
}

export interface ReskillingSession {
  session_id: string;
  status: "pending" | "in_progress" | "completed" | "expired";
  /** Marca del cliente, para quien entra por el link del email. */
  cliente?: {
    slug: string;
    nombre: string;
    logo_url: string | null;
    logo_secundario_url: string | null;
    titulo: string | null;
  } | null;
  iframe_url: string | null;
  questionnaire_url: string | null;
  expires_at: string | null;
  completed_at: string | null;
  report_url: string | null;
  results: ReskillingResults | null;
}

export interface ReskillingResults {
  metrics?: {
    iap?: number;
    ipp?: number;
    match_global?: number;
    perfil_predominante?: string;
    sintesis_perfil?: string;
  };
  archetype?: { name?: string; description?: string };
  ai_potential_index?: { score?: number; level?: string };
  competencies?: Array<{ name: string; score: number; level: string }>;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

async function parse<T>(response: Response): Promise<T> {
  const body: ApiResponse<T> = await response.json().catch(() => ({
    success: false,
    message: "Respuesta inesperada del servidor.",
  }));

  if (!response.ok || !body.success) {
    throw new Error(body.message || "No se pudo completar la operación.");
  }

  return body.data as T;
}

/** Fila del panel del profesional. */
export interface ReskillingEvaluacion {
  session_id: string;
  cliente: string | null;
  cliente_id: number | null;
  nombre: string | null;
  email: string;
  telefono: string | null;
  ocupacion: string | null;
  status: "pending" | "in_progress" | "completed";
  creada_el: string;
  completada_el: string | null;
  expira_el: string | null;
  esta_expirada: boolean;
  dias_sin_actividad: number | null;
  report_url: string | null;
  /** Link que recibe la persona evaluada, si ya se compartió. */
  url_informe_compartido: string | null;
  tiene_resultados: boolean;
  informe_enviado: boolean;
}

export interface ClusterCompetencia {
  nombre: string;
  puntaje: number;
  nivel: string | null;
  /** Análisis personalizado de esta competencia para la persona evaluada. */
  descripcion: string | null;
}

export interface Cluster {
  clave: string;
  label: string;
  icon: string;
  colores: {
    main: string;
    soft: string;
    deep: string;
    gradFrom: string;
    gradTo: string;
  };
  promedio: number;
  competencias: ClusterCompetencia[];
}

/** Un nivel de la evaluación de docentes. */
export interface NivelDocente {
  orden: number;
  sigla: string;
  nombre: string;
  tramo: string;
  estado: string;
  autopercepcion?: { puntaje?: number; banda?: string; recomendacion?: string };
  desempeno?: {
    estado?: string;
    puntos?: number;
    maximo?: number;
    sobre_diez?: number;
    puntaje_100?: number;
    banda?: string;
    no_evaluables?: number;
    indicadores?: Array<{
      clave: string;
      letra: string;
      nombre: string;
      valor: number | string;
    }>;
  };
  gap?: {
    valor?: number;
    banda?: string;
    banda_clave?: string;
    sentido?: string;
    texto?: string;
  };
}

export interface TramoDocente {
  tramo: string;
  nombre: string;
  niveles: number;
  completos: number;
  autopercepcion?: number;
  desempeno?: number;
}

export interface ReskillingInforme {
  /** "docente" o "competencias": define qué estructura trae el informe. */
  tipo?: string;
  docente?: { tramos?: TramoDocente[]; niveles?: NivelDocente[] } | null;
  persona: { nombre: string | null; email: string };
  campana: string | null;
  completada_el: string | null;
  report_url: string | null;
  metrics: {
    iap?: number;
    ipp?: number;
    match_global?: number;
    perfil_predominante?: string;
    sintesis_perfil?: string;
  } | null;
  archetype?: Record<string, any>;
  ai_potential_index?: Record<string, any>;
  clusters?: Cluster[];
  /** Solo en la versión del panel: link para compartir con la persona. */
  url_publica?: string | null;
}

export interface ReskillingResumen {
  total: number;
  completadas: number;
  en_curso: number;
  sin_empezar: number;
}

/**
 * Resultado de consultar por email. No incluye URLs de acceso ni session_id:
 * conocer un email no acredita que la evaluación sea propia, así que el link
 * de continuación viaja únicamente por email.
 */
export interface ReskillingLookup {
  has_session: boolean;
  status?: "pending" | "in_progress" | "completed";
  /** true si el backend reenvió el link de continuación por email. */
  email_enviado?: boolean;
}

export const reskillingService = {
  /**
   * Consulta si un email ya tiene una evaluación, sin crear nada.
   * Si hay una en curso, el backend reenvía el link por email.
   */
  async lookup(email: string): Promise<ReskillingLookup> {
    const response = await fetch(`${BASE_API_URL}/reskilling/sessions/lookup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email }),
    });

    return parse<ReskillingLookup>(response);
  },

  /** Crea la sesión de evaluación y devuelve la URL del iframe. */
  async createSession(input: ReskillingStudentInput): Promise<ReskillingSession> {
    const response = await fetch(`${BASE_API_URL}/reskilling/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(input),
    });

    return parse<ReskillingSession>(response);
  },

  /** Consulta el estado de la sesión; el backend trae los resultados si ya terminó. */
  async getSession(sessionId: string): Promise<ReskillingSession> {
    const response = await fetch(
      `${BASE_API_URL}/reskilling/sessions/${sessionId}`,
      { headers: { Accept: "application/json" } }
    );

    return parse<ReskillingSession>(response);
  },

  /**
   * Listado de evaluaciones para el panel del profesional.
   * Requiere sesión iniciada.
   */
  async listarEvaluaciones(
    params: {
      search?: string;
      status?: string;
      cliente_id?: string;
      page?: number;
    } = {}
  ) {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    if (params.status) query.set("status", params.status);
    if (params.cliente_id) query.set("cliente_id", params.cliente_id);
    if (params.page) query.set("page", String(params.page));

    const sufijo = query.toString() ? `?${query}` : "";

    // fetchWithAuth ya devuelve el JSON parseado y lanza ante errores HTTP.
    const body = await fetchWithAuth(`/reskilling/evaluations${sufijo}`);

    if (!body?.success) {
      throw new Error(body?.message || "No se pudieron cargar las evaluaciones.");
    }

    return {
      evaluaciones: (body.data ?? []) as ReskillingEvaluacion[],
      resumen: body.resumen as ReskillingResumen,
      meta: (body.meta ?? {
        current_page: 1,
        last_page: 1,
        total: 0,
      }) as { current_page: number; last_page: number; total: number },
    };
  },

  /** Informe completo de una evaluación terminada. Requiere sesión iniciada. */
  async obtenerInforme(sessionId: string): Promise<ReskillingInforme> {
    const body = await fetchWithAuth(
      `/reskilling/evaluations/${sessionId}/report`
    );

    if (!body?.success) {
      throw new Error(body?.message || "No se pudo cargar el informe.");
    }

    return body.data as ReskillingInforme;
  },

  /** Informe público, accesible solo con el token de compartir. */
  async obtenerInformePublico(token: string): Promise<ReskillingInforme> {
    const response = await fetch(`${BASE_API_URL}/reskilling/informe/${token}`, {
      headers: { Accept: "application/json" },
    });

    return parse<ReskillingInforme>(response);
  },

  /** Genera o revoca el link público del informe. */
  async compartirInforme(
    sessionId: string,
    revocar = false
  ): Promise<string | null> {
    const body = await fetchWithAuth(
      `/reskilling/evaluations/${sessionId}/share`,
      { method: "POST", body: JSON.stringify({ revocar }) }
    );

    if (!body?.success) {
      throw new Error(body?.message || "No se pudo generar el link.");
    }

    return body.data?.url ?? null;
  },

  /** Reenvía por email el link para retomar la evaluación. */
  async resendLink(sessionId: string): Promise<ReskillingSession> {
    const response = await fetch(
      `${BASE_API_URL}/reskilling/sessions/${sessionId}/resend`,
      { method: "POST", headers: { Accept: "application/json" } }
    );

    return parse<ReskillingSession>(response);
  },
};
